import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import './Search.css';
import { capitalizeName } from '../lib/format';
import { RateModal, NominateModal } from './RatingModal';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const US_STATES = [
  ['','All States'],
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
  ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],
  ['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],
  ['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],
  ['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],
  ['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],
  ['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],['DC','DC'],
];

const LINES = [
  'Property (Residential)',
  'Property (Commercial)',
  'Auto',
  'Workers Compensation',
  'Marine',
  'Flood',
  'Wind/Hurricane',
  'Hail',
  'Fire',
  'Liability',
  'Specialty/Other',
];

// ─── STAR DISPLAY ─────────────────────────────────────────────────────────────
const Stars = ({ rating = 0, size = 12 }) => (
  <div className="pcard-stars">
    {[1,2,3,4,5].map(s => {
      const full    = s <= Math.floor(rating);
      const partial = !full && s === Math.ceil(rating);
      const pct     = partial ? Math.round((rating % 1) * 100) : 0;
      return (
        <svg key={s} width={size} height={size} viewBox="0 0 20 20">
          {partial && (
            <defs>
              <linearGradient id={`sg-${s}`} x1="0" x2="1">
                <stop offset={`${pct}%`} stopColor="var(--gold)" />
                <stop offset={`${pct}%`} stopColor="var(--border)" />
              </linearGradient>
            </defs>
          )}
          <polygon
            points="10,1 12.9,7 19.5,7.6 14.5,12 16.2,18.5 10,15 3.8,18.5 5.5,12 0.5,7.6 7.1,7"
            fill={full ? 'var(--gold)' : partial ? `url(#sg-${s})` : 'var(--border)'}
            stroke="var(--gold)" strokeWidth="0.5"
          />
        </svg>
      );
    })}
  </div>
);

// ─── CONTACT INFO MODAL ───────────────────────────────────────────────────────
const ContactInfoModal = ({ profile, onClose, onReveal }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    onReveal(profile.id)
      .then(data => { if (!cancelled) setContact(data); })
      .catch(err  => { if (!cancelled) setError(err.message || 'Unable to load contact info.'); })
      .finally(()  => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [profile.id, onReveal]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleOverlay = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="modal-overlay" onClick={handleOverlay} style={{ zIndex: 1100 }}>
      <div className="modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
          <div className="modal-identity">
            <div className="modal-name" style={{ fontSize: '16px' }}>
              Contact {capitalizeName(profile.display_name || 'this umpire')}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: '24px' }}>
          {loading && <div style={{ color: 'var(--muted)', fontSize: '14px' }}>Loading contact info…</div>}

          {error && (
            error.toLowerCase().includes('not authenticated') ? (
              <div style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--navy)' }}>Sign in to view contact info</strong><br />
                Create a free account to see how to reach this umpire.
              </div>
            ) : error.toLowerCase().includes('contact sharing') ? (
              <div style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: 1.6 }}>
                This umpire hasn't enabled contact sharing yet.
              </div>
            ) : (
              <div style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: 1.6 }}>
                {error}
              </div>
            )
          )}

          {!loading && !error && !contact && (
            <div style={{ fontSize: '14px', color: 'var(--muted)' }}>No contact information found.</div>
          )}

          {contact && !loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {contact.contact_email && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>Email</div>
                  <div style={{ fontSize: '15px', color: 'var(--navy)', fontFamily: 'var(--serif)', wordBreak: 'break-all' }}>
                    {contact.contact_email}
                  </div>
                </div>
              )}
              {contact.contact_phone && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>Phone</div>
                  <div style={{ fontSize: '15px', color: 'var(--navy)', fontFamily: 'var(--serif)' }}>
                    {contact.contact_phone}
                  </div>
                </div>
              )}
              {!contact.contact_email && !contact.contact_phone && (
                <div style={{ fontSize: '14px', color: 'var(--muted)' }}>This umpire hasn't added contact info yet.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── PREMIUM CONTACT BUTTONS ─────────────────────────────────────────────────
const PremiumContactButtons = ({ profile, onReveal, btnClass }) => {
  const [loadingType, setLoadingType] = useState(null);

  const reveal = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setLoadingType(type);
    try {
      const contact = await onReveal(profile.id);
      if (!contact) return;
      const name = capitalizeName(profile.display_name || 'there');
      if (type === 'email' && contact.contact_email) {
        const subject = encodeURIComponent(`Umpire Consideration — UmpireIQ`);
        const body = encodeURIComponent(
          `Hi ${name},\n\nI found your profile through UmpireIQ and would like to consider you as an umpire in an upcoming appraisal. Please reach out at your earliest convenience to discuss availability.\n\nThank you,`
        );
        window.location.href = `mailto:${contact.contact_email}?subject=${subject}&body=${body}`;
      } else if (type === 'phone' && contact.contact_phone) {
        window.location.href = `tel:${contact.contact_phone}`;
      } else {
        alert(`This umpire has not added ${type === 'email' ? 'an email address' : 'a phone number'} yet.`);
      }
    } catch (err) {
      const msg = err.message || '';
      alert(msg.toLowerCase().includes('not authenticated')
        ? 'Please sign in to contact this umpire.'
        : msg || 'Unable to load contact info right now.');
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <>
      {profile.show_email && (
        <a href="#" className={btnClass} title="Send email" onClick={e => reveal(e, 'email')} aria-busy={loadingType === 'email'}>
          {loadingType === 'email' ? '…' : '✉️'}
        </a>
      )}
      {profile.show_phone && (
        <a href="#" className={btnClass} title="Call" onClick={e => reveal(e, 'phone')} aria-busy={loadingType === 'phone'}>
          {loadingType === 'phone' ? '…' : '📞'}
        </a>
      )}
    </>
  );
};

// ─── TESTIMONIAL HELPERS ─────────────────────────────────────────────────────
// "Joel A. Perez" → "Joel P."  /  "John Smith" → "John S."  /  "Mary" → "Mary"
const formatAuthorName = (displayName) => {
  if (!displayName) return 'Anonymous';
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0];
  const firstName = words[0];
  const lastName  = words[words.length - 1].replace(/\.$/, ''); // strip trailing period
  return `${firstName} ${lastName[0].toUpperCase()}.`;
};

const TRUNCATE_AT = 280; // ~4 lines at modal width

const TestimonialCard = ({ t }) => {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = t.content.length > TRUNCATE_AT;

  return (
    <div style={{ background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: 'var(--navy)',
          lineHeight: 1.6,
          fontFamily: 'var(--serif)',
          fontStyle: 'italic',
          ...(needsTruncation && !expanded ? {
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } : {}),
        }}>
          "{t.content}"
        </p>
        {needsTruncation && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{ background: 'none', border: 'none', padding: '4px 0 0', fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--gold)', cursor: 'pointer', fontWeight: 500 }}
          >
            {expanded ? 'Read less' : 'Read more'}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', color: 'var(--muted)' }}>— {t.author_name}</span>
        <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
          {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
        </span>
      </div>
      {t.owner_reply && (
        <div style={{ borderLeft: '3px solid var(--gold)', paddingLeft: '10px', marginTop: '4px' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>Response</div>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--navy)', lineHeight: 1.6 }}>{t.owner_reply}</p>
        </div>
      )}
    </div>
  );
};

// ─── TESTIMONIAL MODAL ───────────────────────────────────────────────────────
const TestimonialModal = ({ profile, currentUserSide, onClose, onSubmit }) => {
  const [content,             setContent]             = useState('');
  const [authorSideOverride,  setAuthorSideOverride]  = useState('Carrier');
  const [submitting,          setSubmitting]          = useState(false);
  const [error,               setError]               = useState('');
  const [success,             setSuccess]             = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleOverlay = (e) => { if (e.target === e.currentTarget) onClose(); };

  const handleSubmit = async () => {
    if (content.trim().length < 20) { setError('Please write at least 20 characters.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const override = currentUserSide === 'Both' ? authorSideOverride : undefined;
      await onSubmit(profile.id, content.trim(), override);
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('409') || msg.toLowerCase().includes('conflict') || msg.toLowerCase().includes('non-2xx')) {
        setError('Nice try. 😏 You already left a recommendation for this umpire.');
      } else {
        setError(msg || 'Failed to submit. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sideLabel = (s) => s === 'Carrier' ? 'Carrier / Independent Adjuster' : 'Public Adjuster / Policyholder';

  return (
    <div className="modal-overlay" onClick={handleOverlay} style={{ zIndex: 1100 }}>
      <div className="modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
          <div className="modal-identity">
            <div className="modal-name" style={{ fontSize: '16px' }}>
              Leave a recommendation
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>
              for {capitalizeName(profile.display_name || 'this umpire')}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--navy)', fontFamily: 'var(--serif)', fontSize: '16px' }}>
              ✓ Recommendation submitted — thank you!
            </div>
          ) : (
            <>
              {!currentUserSide && (
                <div style={{ fontSize: '13px', color: '#b45309', background: '#fef3c7', borderRadius: 'var(--radius-sm)', padding: '12px' }}>
                  Your profile must have an industry side set (Carrier or Policyholder) before you can leave a recommendation. Update your profile first.
                </div>
              )}

              {currentUserSide === 'Both' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
                    I am writing this as a…
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['Carrier', 'Policyholder'].map(s => (
                      <button
                        key={s}
                        onClick={() => setAuthorSideOverride(s)}
                        style={{
                          flex: 1, padding: '8px', fontSize: '12px', cursor: 'pointer',
                          fontFamily: 'var(--sans)', borderRadius: 'var(--radius-sm)',
                          border: `1.5px solid ${authorSideOverride === s ? 'var(--navy)' : 'var(--border)'}`,
                          background: authorSideOverride === s ? 'var(--navy)' : 'transparent',
                          color: authorSideOverride === s ? 'var(--white)' : 'var(--muted)',
                          transition: 'all 0.2s',
                        }}
                      >
                        {sideLabel(s)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {currentUserSide && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
                    Your recommendation
                  </label>
                  <textarea
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    placeholder="Share your experience working with this umpire. Be specific and professional."
                    rows={5}
                    maxLength={1000}
                    style={{
                      width: '100%', resize: 'vertical', padding: '12px',
                      fontFamily: 'var(--sans)', fontSize: '14px', lineHeight: 1.6,
                      border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
                      background: 'var(--cream)', color: 'var(--navy)',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                    onFocus={e => { e.target.style.borderColor = 'var(--navy)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      {content.length}/1000 · minimum 20 characters
                    </span>
                    {currentUserSide !== 'Both' && (
                      <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                        Writing as: {sideLabel(currentUserSide)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div style={{ fontSize: '13px', color: '#dc2626', background: '#fee2e2', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
                  {error}
                </div>
              )}

              {currentUserSide && (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || content.trim().length < 20}
                  style={{
                    padding: '10px 20px', background: 'var(--navy)', color: 'var(--white)',
                    border: 'none', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)',
                    fontSize: '14px', fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: (submitting || content.trim().length < 20) ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {submitting ? 'Submitting…' : 'Submit recommendation'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── LICENSE TYPE LABEL ───────────────────────────────────────────────────────
const licenseTypeLabel = (lic) =>
  lic?.license_type_detail ? `${lic.license_type} — ${lic.license_type_detail}` : lic?.license_type;

// ─── PROFILE MODAL ────────────────────────────────────────────────────────────
const ProfileModal = ({ profile, onClose, isPremiumViewer, onReveal, session, onTestimonial, currentUserId, isVerifiedViewer }) => {
  if (!profile) return null;

  const [showContactSheet,    setShowContactSheet]    = useState(false);
  const [testimonials,        setTestimonials]        = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [modalTab,            setModalTab]            = useState('profile');
  const [hasTestimonial, setHasTestimonial] = useState(false);

  
  useEffect(() => {
    setModalTab('profile');
  }, [profile.id]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const { data } = await supabase
          .from('testimonials')
          .select('id, content, attributed_side, author_user_id, created_at, is_pinned, owner_reply')
          .eq('target_profile_id', profile.id)
          .eq('is_visible', true)
          .order('is_pinned', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(20);

        if (cancelled) return;

        if (!data || data.length === 0) {
          setTestimonials([]);
          return;
        }

        const authorIds = [...new Set(data.map(t => t.author_user_id))];
        const { data: authorProfiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', authorIds);

        if (cancelled) return;

        const nameMap = {};
        authorProfiles?.forEach(p => { nameMap[p.user_id] = p.display_name; });

        setTestimonials(data.map(t => ({
          ...t,
          author_name: formatAuthorName(nameMap[t.author_user_id]),
        })));

         if (currentUserId) {
      const { data: existing } = await supabase
        .from('testimonials')
        .select('id')
        .eq('target_profile_id', profile.id)
        .eq('author_user_id', currentUserId)
        .maybeSingle();
      if (!cancelled) setHasTestimonial(!!existing);
    }
        
      } catch (err) {
        console.error('Testimonials fetch error:', err);
        if (!cancelled) setTestimonials([]);
      } finally {
        if (!cancelled) setTestimonialsLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [profile.id]);

  const initials = (profile.display_name || 'U')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const homeLicense = profile.licenses?.find(l => l.is_home);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const testimonialCount = testimonialsLoading ? 0 : testimonials.length;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal">

        {/* Header */}
        <div className="modal-header">
          <div className="modal-avatar">
            {profile.headshot_url
              ? <img src={profile.headshot_url} alt={capitalizeName(profile.display_name)} />
              : initials
            }
          </div>
<div className="modal-identity">
  <div className="modal-name">{capitalizeName(profile.display_name || 'Unnamed Professional')}</div>
  <div className="modal-badges" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
    {homeLicense && <span className="pcard-badge type">{licenseTypeLabel(homeLicense)}</span>}
    {homeLicense && <span className="pcard-badge state">{homeLicense.state}</span>}
    {profile.user_roles?.primary_role && (
      <span className="pcard-badge role">{profile.user_roles.primary_role}</span>
    )}
    {profile.peer_reviewed && (
      <span className="pcard-badge type" style={{ background: 'var(--gold)' }}>Peer Reviewed</span>
    )}
    {profile.show_contact && (
      (profile.profile_tier === 'premium' || profile.profile_tier === 'peer_reviewed')
        ? <PremiumContactButtons profile={profile} onReveal={onReveal} btnClass="modal-contact-btn" />
        : <a href="#" className="modal-contact-link" onClick={e => { e.preventDefault(); e.stopPropagation(); setShowContactSheet(true); }}>See contact info</a>
    )}
  </div>
</div>

          <button className="modal-close" onClick={onClose}>×</button>
        
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 24px' }}>
          <button
            className={`dashboard-tab ${modalTab === 'profile' ? 'active' : ''}`}
            onClick={() => setModalTab('profile')}
          >
            Profile
          </button>
          <button
            className={`dashboard-tab ${modalTab === 'testimonials' ? 'active' : ''}`}
            onClick={() => setModalTab('testimonials')}
          >
            Recommendations {testimonialCount > 0 ? `(${testimonialCount})` : ''}
          </button>
        </div>

        {/* Profile Tab */}
          {/*ratings section*/}
        {modalTab === 'profile' && (
          <div className="modal-body">

            <div className="modal-section">
              <div className="modal-section-title">Ratings</div>
              <div className="modal-ratings">
                {[
                  { label: 'Overall', rating: profile.overall_rating_avg || 0 },
                  { label: 'IA',      rating: profile.ia_rating_avg || 0 },
                  { label: 'PA',      rating: profile.pa_rating_avg || 0 },
                ].map(r => (
                  <div key={r.label} className="modal-rating-box">
                    <div className="modal-rating-label">{r.label}</div>
                    <div className="modal-rating-stars"><Stars rating={r.rating} size={11} /></div>
                    <div className="modal-rating-avg">{r.rating || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          
          {/*AI Testimonial Analysis*/}
        {!testimonialsLoading && testimonials.length > 0 && (() => {
          const carrierCount     = testimonials.filter(t => t.attributed_side === 'Carrier').length;
          const policyholderCount = testimonials.filter(t => t.attributed_side === 'Policyholder').length;
          const total            = testimonials.length;

          return (
            <div className="modal-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <div className="modal-section-title" style={{ marginBottom: 0 }}>AI Recommendation Analysis</div>
                <span style={{
                  fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'var(--white)', background: 'var(--gold)',
                  padding: '2px 6px', borderRadius: '99px',
                }}>Beta</span>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{
                  flex: 1, background: 'var(--cream)', borderRadius: 'var(--radius-md)',
                  padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px',
                }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
                    Carrier Perspective
                  </div>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: '24px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1 }}>
                    {carrierCount}
                  </div>
                  <div style={{ height: '4px', background: 'var(--border)', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: '99px', background: 'var(--navy)',
                      width: total > 0 ? `${Math.round((carrierCount / total) * 100)}%` : '0%',
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>

                <div style={{
                  flex: 1, background: 'var(--cream)', borderRadius: 'var(--radius-md)',
                  padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px',
                }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
                    Policyholder Perspective
                  </div>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: '24px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1 }}>
                    {policyholderCount}
                  </div>
                  <div style={{ height: '4px', background: 'var(--border)', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: '99px', background: 'var(--gold)',
                      width: total > 0 ? `${Math.round((policyholderCount / total) * 100)}%` : '0%',
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.5 }}>
                Perspective is algorithmically derived from reviewer industry side and recommendation sentiment. Full AI analysis coming soon.
              </div>
            </div>
          );
        })()}


            {profile.bio && (
              <div className="modal-section">
                <div className="modal-section-title">About</div>
                <p className="modal-bio">{profile.bio}</p>
              </div>
            )}

            {profile.lines_of_business?.length > 0 && (
              <div className="modal-section">
                <div className="modal-section-title">Lines of Business</div>
                <div className="modal-tags">
                  {profile.lines_of_business.map(l => <span key={l} className="modal-tag">{l}</span>)}
                </div>
              </div>
            )}

            {profile.certifications?.length > 0 && (
              <div className="modal-section">
                <div className="modal-section-title">Certifications</div>
                <div className="modal-tags">
                  {profile.certifications.map(c => <span key={c} className="modal-tag">{c}</span>)}
                </div>
              </div>
            )}

            {profile.licenses?.length > 0 && (
              <div className="modal-section">
                <div className="modal-section-title">Licenses & Credentials</div>
                <div className="modal-license-list">
                  {[...profile.licenses]
                    .sort((a, b) => `${a.license_type}${a.state}`.localeCompare(`${b.license_type}${b.state}`))
                    .map((lic, i) => (
                      <div key={i} className="modal-license-item">
                        <div>
                          <div className="modal-license-name">
                            {licenseTypeLabel(lic)} {lic.is_home ? '🏠' : ''}
                          </div>
                          <div className="modal-license-detail">
                            {lic.state}
                            {lic.expiry_date ? ` · Expires ${new Date(lic.expiry_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''}
                          </div>
                        </div>
                        {lic.verified && <span className="modal-verified">Verified</span>}
                      </div>
                    ))
                  }
                </div>
              </div>
            )}

            <div className="modal-section">
              <div className="modal-section-title">Activity</div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1, background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: '28px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1 }}>
                    {profile.assignment_count || 0}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '4px' }}>
                    Assignments
                  </div>
                </div>
                {profile.years_experience && (
                  <div style={{ flex: 1, background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '14px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--serif)', fontSize: '28px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1 }}>
                      {profile.years_experience}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '4px' }}>
                      Years Exp.
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {/* Testimonials Tab */}
        {modalTab === 'testimonials' && (
          <div className="modal-body modal-body-testimonials">

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
              {session && profile.user_id !== currentUserId && !hasTestimonial && profile.user_roles?.primary_role !== 'Appraiser' && (
                isVerifiedViewer ? (
                  <button
                    onClick={() => onTestimonial(profile)}
                    style={{
                      padding: '6px 12px', background: 'transparent', border: '1.5px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '12px',
                      color: 'var(--muted)', cursor: 'pointer', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.target.style.borderColor = 'var(--navy)'; e.target.style.color = 'var(--navy)'; }}
                    onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--muted)'; }}
                  >
                    ✍ Write one
                  </button>
                ) : (
                  <div style={{ fontSize: '12px', color: '#b45309', background: '#fef3c7', border: '1px solid #d97706', borderRadius: 'var(--radius-sm)', padding: '7px 12px' }}>
                    🔒 Verify your license to leave recommendations
                  </div>
                )
              )}
            </div>

            {testimonialsLoading ? (
              <div style={{ color: 'var(--muted)', fontSize: '13px' }}>Loading…</div>
            ) : testimonials.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: '13px' }}>No recommendations yet. Be the first.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {testimonials.map(t => (
                  <TestimonialCard key={t.id} t={t} />
                ))}
              </div>
            )}

          </div>
        )}

      </div>

      {showContactSheet && (
        <ContactInfoModal
          profile={profile}
          onClose={() => setShowContactSheet(false)}
          onReveal={onReveal}
        />
      )}
    </div>
  );
};

// ─── PROFILE CARD ─────────────────────────────────────────────────────────────
const ProfileCard = ({ profile, onClick, session, onRate, onTestimonial, isPremiumViewer, onReveal, currentUserId, isVerifiedViewer }) => {
  const initials = (profile.display_name || 'U')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const homeLicense = profile.licenses?.find(l => l.is_home);

  return (
    <div className="pcard" onClick={() => onClick(profile)}>

      {profile.peer_reviewed && (
        <div className="pcard-peer-badge">Peer Reviewed</div>
      )}

      <div className="pcard-avatar">
        {profile.headshot_url
          ? <img src={profile.headshot_url} alt={capitalizeName(profile.display_name)} />
          : initials
        }
      </div>

      <div className="pcard-badges">
        {homeLicense && <span className="pcard-badge type">{homeLicense.license_type}</span>}
        {homeLicense && <span className="pcard-badge state">{homeLicense.state}</span>}
        {profile.user_roles?.primary_role && (
          <span className="pcard-badge role">{profile.user_roles.primary_role}</span>
        )}
      </div>

      <div className="pcard-name">{capitalizeName(profile.display_name || 'Unnamed Professional')}</div>
      {!profile.is_claimed && (
        <div className="pcard-unclaimed">Unclaimed Profile</div>
      )}
      <div className="pcard-role">
        {profile.user_roles?.side || '—'}
        {profile.assignment_count > 0 ? ` · ${profile.assignment_count} assignments` : ''}
      </div>

      <div className="pcard-divider" />

      {/* Star rating */}
      <div className="pcard-rating">
        <Stars rating={profile.overall_rating_avg || 0} size={13} />
        <span className="pcard-rating-avg">
          {profile.overall_rating_avg ? profile.overall_rating_avg.toFixed(1) : '—'}
        </span>
        <span className="pcard-rating-count">({profile.rating_count || 0} ratings)</span>
      </div>

      {/* IA/PA endorsements */}
      <div className="pcard-endorsements">
        <div className="pcard-endorse-box">
          <div className="pcard-endorse-num">{profile.ia_rating_avg ? profile.ia_rating_avg.toFixed(1) : '—'}</div>
          <div className="pcard-endorse-label">IA Avg</div>
        </div>
        <div className="pcard-endorse-box">
          <div className="pcard-endorse-num">{profile.pa_rating_avg ? profile.pa_rating_avg.toFixed(1) : '—'}</div>
          <div className="pcard-endorse-label">PA Avg</div>
        </div>
      </div>

      {/* Premium umpires: contact front and center on the card */}
      {profile.show_contact && (profile.profile_tier === 'premium' || profile.profile_tier === 'peer_reviewed') && (
        <div className="pcard-contact">
          <PremiumContactButtons profile={profile} onReveal={onReveal} btnClass="pcard-contact-btn" />
        </div>
      )}

      {session && currentUserId && profile.user_id !== currentUserId && (() => {
        const isAppraiser = profile.user_roles?.primary_role === 'Appraiser';
        const blocked     = isAppraiser || !isVerifiedViewer;
        const rateTitle   = isAppraiser ? 'Rating available for umpires only' : !isVerifiedViewer ? 'Verify your license to rate umpires' : '';
        const recTitle    = isAppraiser ? 'Recommendations available for umpires only' : !isVerifiedViewer ? 'Verify your license to leave recommendations' : '';
        const disabledColor = 'var(--border)';
        const activeColor   = 'var(--muted)';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
            {!isVerifiedViewer && !isAppraiser && (
              <div style={{ fontSize: '11px', color: 'var(--muted)', background: 'var(--cream)', borderRadius: 'var(--radius-sm)', padding: '6px 8px', textAlign: 'center', lineHeight: 1.5 }}>
                🔒 Verify your license to rate &amp; recommend
              </div>
            )}
            <button
              onClick={e => { e.stopPropagation(); if (!blocked) onRate(profile); }}
              disabled={blocked}
              title={rateTitle}
              style={{ width: '100%', padding: '8px', background: 'transparent', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '12px', color: blocked ? disabledColor : activeColor, cursor: blocked ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { if (!blocked) { e.target.style.borderColor = 'var(--gold)'; e.target.style.color = 'var(--gold)'; }}}
              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = blocked ? disabledColor : activeColor; }}
            >
              ★ Rate this umpire
            </button>
            <button
              onClick={e => { e.stopPropagation(); if (!blocked) onTestimonial(profile); }}
              disabled={blocked}
              title={recTitle}
              style={{ width: '100%', padding: '8px', background: 'transparent', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '12px', color: blocked ? disabledColor : activeColor, cursor: blocked ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { if (!blocked) { e.target.style.borderColor = 'var(--navy)'; e.target.style.color = 'var(--navy)'; }}}
              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = blocked ? disabledColor : activeColor; }}
            >
              ✍ Leave a recommendation
            </button>
          </div>
        );
      })()}

    </div>
  );
};

// ─── SEARCH PAGE ──────────────────────────────────────────────────────────────
const Search = ({ onNavigate, session }) => {
  const [profiles,    setProfiles]    = useState([]);
  const [filtered,    setFiltered]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [searchText,  setSearchText]  = useState('');
  const [rateTarget,        setRateTarget]        = useState(null);
  const [testimonialTarget, setTestimonialTarget] = useState(null);
  const [showNominate,      setShowNominate]      = useState(false);
  const [currentUser,       setCurrentUser]       = useState(null);
  const [userLicenseType,   setUserLicenseType]   = useState(null);
  const [currentUserSide,   setCurrentUserSide]   = useState(null);
  const [isPremiumViewer,   setIsPremiumViewer]   = useState(false);
  const [currentUserBadge,  setCurrentUserBadge]  = useState(null);

  const [filters, setFilters] = useState({
    state:        '',
    licenseType:  '',
    primaryRole:  '',
    side:         '',
    certification:'',
    line:         '',
    peerReviewed: false,
  });

  const [sortBy, setSortBy] = useState('rating');

  // ─── FETCH ALL VISIBLE PROFILES ──────────────────────────────────────────
const fetchProfiles = useCallback(async () => {
    setLoading(true);

  const { data: profileData, error } = await supabase
  .from('profiles')
  .select(`
    id, display_name, headshot_url, bio, certifications,
    specialties, lines_of_business, profile_tier, peer_reviewed,
    show_contact, show_email, show_phone, assignment_count,
    years_experience, user_id, is_claimed,
    overall_rating_avg, ia_rating_avg, pa_rating_avg, rating_count
  `)
  .eq('is_visible', true)
  .limit(100);

  if (error) {
    console.error('Search fetch error:', error.message);
    setLoading(false);
    return;
  }

  // Fetch related data for each profile
  const enriched = await Promise.all(profileData.map(async (p) => {
    const [{ data: roles }, { data: licenses }] = await Promise.all([
      supabase.from('user_roles').select('primary_role, side').eq('user_id', p.user_id).maybeSingle(),
      supabase.rpc('get_public_license_info', { p_user_ids: [p.user_id] }),
    ]);
    return { ...p, user_roles: roles, licenses: licenses || [] };
  }));

  setProfiles(enriched);
  setFiltered(enriched);
  setLoading(false);
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);
    useEffect(() => {
    const getUser = async () => {
      if (!session) return;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();
      if (userData) setCurrentUser(userData);

      const { data: licData } = await supabase
        .from('licenses')
        .select('license_type')
        .eq('user_id', userData?.id)
        .eq('is_home', true)
        .single();
      if (licData) setUserLicenseType(licData.license_type);

      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('side')
        .eq('user_id', userData?.id)
        .single();
      if (rolesData?.side) setCurrentUserSide(rolesData.side);

      const { data: ownProfile } = await supabase
        .from('profiles')
        .select('profile_tier, verification_badge')
        .eq('user_id', userData?.id)
        .single();
      setIsPremiumViewer(ownProfile?.profile_tier === 'premium' || ownProfile?.profile_tier === 'peer_reviewed');
      setCurrentUserBadge(ownProfile?.verification_badge || null);
    };
    getUser();
  }, [session]);

  // Calls the testimonial-submit Edge Function.
  const submitTestimonial = useCallback(async (targetProfileId, content, authorSideOverride) => {
    const body = { target_profile_id: targetProfileId, content };
    if (authorSideOverride) body.author_side_override = authorSideOverride;
    const { data, error } = await supabase.functions.invoke('testimonial-submit', { body });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  }, []);

  // "Contact this umpire" — decrypts a target profile's contact info via the
  // get_umpire_contact RPC (premium-only, audit-logged server-side).
  const revealContact = useCallback(async (profileId) => {
    const { data, error } = await supabase.rpc('get_umpire_contact', { p_profile_id: profileId }).single();
    if (error) throw new Error(error.message);
    return data;
  }, []);

  // ─── FILTER + SORT ────────────────────────────────────────────────────────
  useEffect(() => {
    let results = [...profiles];

    // Text search
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      results = results.filter(p =>
        p.display_name?.toLowerCase().includes(q) ||
        p.bio?.toLowerCase().includes(q) ||
        p.certifications?.some(c => c.toLowerCase().includes(q))
      );
    }

    // State filter
    if (filters.state) {
      results = results.filter(p =>
        p.licenses?.some(l => l.state === filters.state)
      );
    }

    // License type filter
    if (filters.licenseType) {
      results = results.filter(p =>
        p.licenses?.some(l => l.license_type === filters.licenseType)
      );
    }

    // Primary role filter
    if (filters.primaryRole) {
      results = results.filter(p =>
        p.user_roles?.primary_role === filters.primaryRole
      );
    }

    // Side filter
    if (filters.side) {
      results = results.filter(p =>
        p.user_roles?.side === filters.side || p.user_roles?.side === 'Both'
      );
    }

    // Certification filter
    if (filters.certification) {
      results = results.filter(p =>
        p.certifications?.includes(filters.certification)
      );
    }

    // Line of business filter
    if (filters.line) {
      results = results.filter(p =>
        p.lines_of_business?.includes(filters.line)
      );
    }

    // Peer reviewed toggle
    if (filters.peerReviewed) {
      results = results.filter(p => p.peer_reviewed);
    }

    // Sort
    if (sortBy === 'assignments') {
      results.sort((a, b) => (b.assignment_count || 0) - (a.assignment_count || 0));
    } else if (sortBy === 'peer') {
      results.sort((a, b) => (b.peer_reviewed ? 1 : 0) - (a.peer_reviewed ? 1 : 0));
    }
    // Default: rating (all zero for now, will update when ratings are built)

    setFiltered(results);
  }, [profiles, filters, searchText, sortBy]);

  const updateFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const clearFilters = () => {
    setFilters({ state: '', licenseType: '', primaryRole: '', side: '', certification: '', line: '', peerReviewed: false });
    setSearchText('');
  };

  const hasFilters = Object.values(filters).some(v => v !== '' && v !== false) || searchText;

  return (
    <div className="search-page">

      {/* Nav */}
      <nav className="search-nav">
        <div className="search-nav-logo" onClick={() => onNavigate('home')}>
          Umpire<span>IQ</span>
        </div>
        <div className="search-nav-right">
          {session ? (
            <button className="search-nav-btn primary" onClick={() => onNavigate('dashboard')}>
              My Profile
            </button>
          ) : (
            <>
              <button className="search-nav-btn" onClick={() => onNavigate('login')}>Sign In</button>
              <button className="search-nav-btn primary" onClick={() => onNavigate('signup')}>Sign Up</button>
            </>
          )}
        </div>
      </nav>

      {/* Header + search bar */}
      <div className="search-header">
        <div className="search-header-inner">
          <h1>Find a <em>neutral</em> umpire.</h1>
          <p>Search verified appraisal professionals across all 50 states.</p>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name, certification, or specialty..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <button className="search-bar-btn">Search</button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="search-filters">
        <div className="search-filters-inner">

          <select className={`filter-select ${filters.state ? 'active' : ''}`} value={filters.state} onChange={e => updateFilter('state', e.target.value)}>
            {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>

          <select className={`filter-select ${filters.licenseType ? 'active' : ''}`} value={filters.licenseType} onChange={e => updateFilter('licenseType', e.target.value)}>
            <option value="">All License Types</option>
            <option value="PA">Public Adjuster</option>
            <option value="IA">Independent Appraiser</option>
            <option value="Contractor">Contractor</option>
            <option value="Attorney">Attorney</option>
            <option value="Engineer">Engineer</option>
          </select>

          <select className={`filter-select ${filters.primaryRole ? 'active' : ''}`} value={filters.primaryRole} onChange={e => updateFilter('primaryRole', e.target.value)}>
            <option value="">All Roles</option>
            <option value="Appraiser">Appraiser</option>
            <option value="Umpire">Umpire</option>
            <option value="Both">Both</option>
          </select>

          <select className={`filter-select ${filters.side ? 'active' : ''}`} value={filters.side} onChange={e => updateFilter('side', e.target.value)}>
            <option value="">All Sides</option>
            <option value="Policyholder">Insured Side</option>
            <option value="Carrier">Carrier Side</option>
            <option value="Both">Both Sides</option>
          </select>

          <select className={`filter-select ${filters.certification ? 'active' : ''}`} value={filters.certification} onChange={e => updateFilter('certification', e.target.value)}>
            <option value="">All Certifications</option>
            <option value="WIND">WIND</option>
            <option value="IAUA">IAUA</option>
          </select>

          <select className={`filter-select ${filters.line ? 'active' : ''}`} value={filters.line} onChange={e => updateFilter('line', e.target.value)}>
            <option value="">All Lines</option>
            {LINES.map(l => <option key={l} value={l}>{l}</option>)}
          </select>

          <button
            className={`filter-toggle ${filters.peerReviewed ? 'active' : ''}`}
            onClick={() => updateFilter('peerReviewed', !filters.peerReviewed)}
          >
            ✓ Peer Reviewed
          </button>

          {hasFilters && (
            <button className="filter-clear" onClick={clearFilters}>Clear all</button>
          )}

        </div>
      </div>

      {/* Results bar */}
      <div className="search-results-bar">
        <span className="search-results-count">
          {loading ? 'Loading...' : `${filtered.length} professional${filtered.length !== 1 ? 's' : ''} found`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="search-sort">
            <span>Sort by</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="rating">Overall Rating</option>
              <option value="assignments">Most Assignments</option>
              <option value="peer">Peer Reviewed First</option>
            </select>
          </div>
          {session && (
            <button
              onClick={() => setShowNominate(true)}
              style={{ padding: '8px 16px', background: 'var(--gold)', color: 'var(--white)', border: 'none', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
            >
              + Add an umpire
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="search-body">
        {loading ? (
          <div className="search-loading">
            <div className="signup-spinner" />
            Loading professionals...
          </div>
        ) : filtered.length === 0 ? (
          <div className="search-empty">
            <div className="search-empty-icon">🔍</div>
            <h3>No results found</h3>
            <p>Try adjusting your filters or search terms.</p>
          </div>
        ) : (
          <div className="search-grid">
            {filtered.map(profile => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                onClick={setSelected}
                session={session}
                onRate={setRateTarget}
                onTestimonial={setTestimonialTarget}
                isPremiumViewer={isPremiumViewer}
                onReveal={revealContact}
                currentUserId={currentUser?.id}
                isVerifiedViewer={currentUserBadge === 'verified'}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {selected && (
        <ProfileModal
          profile={selected}
          onClose={() => setSelected(null)}
          isPremiumViewer={isPremiumViewer}
          onReveal={revealContact}
          session={session}
          onTestimonial={setTestimonialTarget}
          currentUserId={currentUser?.id}
          isVerifiedViewer={currentUserBadge === 'verified'}
        />
      )}

      {rateTarget && (
        <RateModal
          profile={rateTarget}
          currentUserId={currentUser?.id}
          userRole={userLicenseType}
          onClose={() => setRateTarget(null)}
          onRated={fetchProfiles}
        />
      )}

      {showNominate && (
        <NominateModal
          currentUserId={currentUser?.id}
          onClose={() => setShowNominate(false)}
          onNominated={fetchProfiles}
        />
      )}

      {testimonialTarget && (
        <TestimonialModal
          profile={testimonialTarget}
          currentUserSide={currentUserSide}
          onClose={() => setTestimonialTarget(null)}
          onSubmit={submitTestimonial}
        />
      )}

    </div>
  );
};

export default Search;
